/**
 * YouTube links for the highlight video field (APS-259).
 *
 * The same link shapes as youtubeVideoId in backend utils/coachVideos.js:
 * watch?v=<id>, youtu.be/<id>, /shorts/<id> and /embed/<id>, on youtube.com,
 * any subdomain of it, and youtube-nocookie.com. The server checks again and
 * is the one that counts. This copy puts the message under the input.
 */

const YOUTUBE_ID_RE = /^[A-Za-z0-9_-]{11}$/;
const YOUTUBE_HOST_RE = /^(?:[a-z0-9-]+\.)?youtube(?:-nocookie)?\.com$/;
const SHORT_HOST_RE = /^(?:www\.)?youtu\.be$/;
const MAX_LENGTH = 500;

export const VIDEO_ERROR_HUDL =
  "Coaches on APS can't play Hudl links yet. Upload the video to YouTube (unlisted is fine) and paste that link.";
export const VIDEO_ERROR_OTHER = 'Paste a YouTube link. Coaches on APS play YouTube videos.';

/** The video id in a YouTube link, or null. */
export function youtubeVideoId(url: string | null | undefined): string | null {
  if (typeof url !== 'string') return null;
  let raw = url.trim();
  if (!raw) return null;
  if (!/^[a-z][a-z0-9+.-]*:\/\//i.test(raw)) raw = `https://${raw}`;

  let parsed: URL;
  try {
    parsed = new URL(raw);
  } catch {
    return null;
  }
  if (parsed.protocol !== 'https:' && parsed.protocol !== 'http:') return null;

  const host = parsed.hostname.toLowerCase();
  const parts = parsed.pathname.split('/').filter(Boolean);
  let id: string | null = null;
  if (SHORT_HOST_RE.test(host)) {
    id = parts[0] || null;
  } else if (YOUTUBE_HOST_RE.test(host)) {
    if (parts[0] === 'watch') id = parsed.searchParams.get('v');
    else if (parts[0] === 'shorts' || parts[0] === 'embed') id = parts[1] || null;
  }
  return id && YOUTUBE_ID_RE.test(id) ? id : null;
}

export type VideoLinkCheck =
  | { url: string; id: string | null; error?: undefined; reason?: undefined }
  | { error: string; reason: 'hudl' | 'not_youtube' | 'too_long'; url?: undefined; id?: undefined };

/**
 * Check a pasted link. '' is fine and means "clear it". A good link comes back
 * as https://www.youtube.com/watch?v=<id>, the form the server stores.
 */
export function checkVideoLink(raw: string | null | undefined): VideoLinkCheck {
  const value = (raw || '').trim();
  if (!value) return { url: '', id: null };
  if (value.length > MAX_LENGTH) return { error: VIDEO_ERROR_OTHER, reason: 'too_long' };
  const id = youtubeVideoId(value);
  if (id) return { url: `https://www.youtube.com/watch?v=${id}`, id };
  if (/(^|[./])hudl\.com/i.test(value)) return { error: VIDEO_ERROR_HUDL, reason: 'hudl' };
  return { error: VIDEO_ERROR_OTHER, reason: 'not_youtube' };
}

/** YouTube's own still for a video. */
export function youtubeThumbnail(id: string): string {
  return `https://img.youtube.com/vi/${id}/hqdefault.jpg`;
}
